<template>
    <div class="modal">
        <div class="modal-content">
            <div class="modal-header">
                <h2>재고위치 QR 코드 생성</h2>
                <span class="close" @click="$emit('close')">×</span>
            </div>

            <!-- 위치 입력 영역 -->
            <div class="location-input-area">
                <div class="input-group">
                    <label for="locationInput">재고 위치 입력 :</label>
                    <div class="location-input-container">
                        <input type="text" v-model="locationPrefix" placeholder="가" @keyup.enter="addLocation"
                            class="location-prefix-input" maxlength="2" />
                        <span class="separator">-</span>
                        <input type="text" v-model="locationNumber" placeholder="1" @keyup.enter="addLocation"
                            class="location-number-input" maxlength="3" />
                    </div>
                    <button @click="addLocation" class="add-btn">추가</button>
                </div>

                <!-- 추가된 위치 목록 -->
                <div class="location-list" v-if="locationList.length > 0">
                    <h4>추가된 위치:</h4>
                    <div class="location-tags">
                        <span v-for="(location, index) in locationList" :key="index" class="location-tag">
                            {{ location }}
                            <button @click="removeLocation(index)" class="remove-btn">×</button>
                        </span>
                    </div>
                </div>
            </div>

            <div class="print-controls">
                <!-- 기본 QR 생성 버튼 추가 -->
                <button @click="generateDefaultLocations" class="default-qr-btn">기본 QR 생성</button>
                <button @click="generateLocationQRCodes" :disabled="locationList.length === 0">QR 코드 생성</button>
                <button @click="printAllQRCodes" :disabled="!hasGeneratedQRCodes">전체 인쇄</button>
                <button @click="printSelectedQRCodes" :disabled="!hasSelectedItems">선택 인쇄</button>
                <label class="select-all">
                    <input type="checkbox" v-model="selectAll" @change="toggleSelectAll">
                    전체 선택
                </label>
            </div>

            <div class="qr-codes-container" ref="qrCodesContainer">
                <div class="location-qr-tag" v-for="(item, index) in generatedQRCodes" :key="index"
                    :data-location="item.location">
                    <div class="select-checkbox">
                        <input type="checkbox" v-model="selectedItems[item.location]" @change="updateSelectAll">
                    </div>
                    <div class="qr-tag-content">
                        <div class="location-info">
                            <div class="location-name">{{ item.location }}</div>
                        </div>
                        <div class="qr-code-area">
                            <img v-if="item.qrCodeUrl" :src="item.qrCodeUrl" class="location-qr-code" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import QRCode from 'qrcode';

export default {
    name: 'LocationPrint',

    data() {
        return {
            locationPrefix: '', // 위치 접두사 (가, 나, 다 등)
            locationNumber: '', // 위치 번호 (1, 2, 3 등)
            locationList: [], // 추가된 위치 목록
            generatedQRCodes: [], // 생성된 QR 코드 목록
            selectedItems: {}, // 선택된 아이템 상태
            selectAll: false, // 전체 선택 여부
            hasGeneratedQRCodes: false // QR 코드 생성 여부
        };
    },

    created() {
        // ESC 키 이벤트 리스너 추가
        document.addEventListener('keydown', this.handleEscKey);
    },

    beforeUnmount() {
        // 컴포넌트가 제거될 때 이벤트 리스너 제거
        document.removeEventListener('keydown', this.handleEscKey);
    },

    computed: {
        hasSelectedItems() {
            return Object.values(this.selectedItems).some(value => value);
        }
    },

    methods: {
        // 기본 위치 생성 (가-1 ~ 하-6)
        generateDefaultLocations() {
            this.$swal.fire({
                title: '기본 위치 생성',
                html: `
                    <p>가-1 부터 하-6 까지 총 <strong>84개</strong>의 위치를 생성합니다.</p>
                    <p style="color: #856404; background-color: #fff3cd; padding: 8px; border-radius: 5px; margin: 10px 0; font-size: 16px;">
                        ⚠️ 기존에 추가된 위치는 모두 삭제되고 새로 생성됩니다.
                    </p>
                    <p>계속하시겠습니까?</p>
                `,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: '생성',
                cancelButtonText: '취소',
                confirmButtonColor: '#0f4c5c',
                cancelButtonColor: '#6c757d'
            }).then((result) => {
                if (result.isConfirmed) {
                    this.createDefaultLocationList();
                }
            });
        },

        // 기본 위치 목록 생성
        createDefaultLocationList() {
            // 기존 위치 목록 초기화
            this.locationList = [];
            this.generatedQRCodes = [];
            this.selectedItems = {};
            this.hasGeneratedQRCodes = false;

            // 한글 자음 순서 (가-하)
            const consonants = ['가', '나', '다', '라', '마', '바', '사', '아', '자', '차', '카', '타', '파', '하'];

            // 각 자음에 대해 1-6번까지 생성
            consonants.forEach(consonant => {
                for (let i = 1; i <= 6; i++) {
                    const location = `${consonant}-${i}`;
                    this.locationList.push(location);
                }
            });

            this.$swal.fire({
                title: '생성 완료',
                text: `총 ${this.locationList.length}개의 재고 위치가 생성되었습니다.`,
                icon: 'success',
                confirmButtonText: '확인',
            });
        },

        // 위치 추가
        addLocation() {
            const prefix = this.locationPrefix.trim();
            const number = this.locationNumber.trim();

            if (!prefix || !number) {
                this.$swal.fire({
                    title: '입력 오류',
                    text: '위치와 번호를 모두 입력해주세요.',
                    icon: 'warning',
                    confirmButtonText: '확인'
                });
                return;
            }

            const location = `${prefix}-${number}`;

            if (!this.locationList.includes(location)) {
                this.locationList.push(location);
                this.locationPrefix = '';
                this.locationNumber = '';

                // 다음 입력을 위해 첫 번째 인풋에 포커스
                this.$nextTick(() => {
                    const prefixInput = document.querySelector('.location-prefix-input');
                    if (prefixInput) {
                        prefixInput.focus();
                    }
                });
            } else {
                this.$swal.fire({
                    title: '중복 위치',
                    text: '이미 추가된 위치입니다.',
                    icon: 'warning',
                    confirmButtonText: '확인'
                });
            }
        },

        // 위치 제거
        removeLocation(index) {
            this.locationList.splice(index, 1);
            // 해당 위치의 QR 코드도 제거
            this.generatedQRCodes = this.generatedQRCodes.filter((_, i) => i !== index);
            this.hasGeneratedQRCodes = this.generatedQRCodes.length > 0;
        },

        // 위치별 QR 코드 생성
        async generateLocationQRCodes() {
            try {
                this.generatedQRCodes = [];
                this.selectedItems = {};

                for (const location of this.locationList) {
                    const qrData = {
                        location: location
                    };

                    try {
                        // JSON 문자열을 Base64로 인코딩하여 한글 깨짐 방지
                        const jsonString = JSON.stringify(qrData);
                        const encodedData = btoa(unescape(encodeURIComponent(jsonString)));

                        const qrCodeUrl = await QRCode.toDataURL(encodedData, {
                            errorCorrectionLevel: 'L',
                            type: 'image/png',
                            quality: 0.92,
                            margin: 1,
                            width: 200,
                            color: {
                                dark: '#000000',
                                light: '#FFFFFF'
                            }
                        });

                        this.generatedQRCodes.push({
                            location: location,
                            qrCodeUrl: qrCodeUrl,
                            qrData: qrData
                        });

                        // 선택 상태 초기화
                        this.selectedItems[location] = false;

                    } catch (error) {
                        console.error(`위치 ${location} QR 코드 생성 오류:`, error);
                    }
                }

                this.hasGeneratedQRCodes = this.generatedQRCodes.length > 0;

                this.$swal.close();

                setTimeout(() => {
                    if (this.hasGeneratedQRCodes) {
                        this.$swal.fire({
                            title: '생성 완료',
                            text: `${this.generatedQRCodes.length}개 위치의 QR 코드가 생성되었습니다.`,
                            icon: 'success',
                            confirmButtonText: '확인'
                        });
                    } else {
                        this.$swal.fire({
                            title: '오류',
                            text: 'QR 코드 생성에 실패했습니다.',
                            icon: 'error',
                            confirmButtonText: '확인'
                        });
                    }
                }, 100);

            } catch (error) {
                console.error('QR 코드 생성 중 오류:', error);

                this.$swal.close();

                setTimeout(() => {
                    this.$swal.fire({
                        title: '오류',
                        text: 'QR 코드 생성 중 오류가 발생했습니다.',
                        icon: 'error',
                        confirmButtonText: '확인'
                    });
                }, 100);
            }
        },

        // 전체 QR 코드 인쇄
        printAllQRCodes() {
            const allContent = this.$refs.qrCodesContainer.innerHTML;
            this.printContent(allContent);
        },

        // 선택된 QR 코드 인쇄
        printSelectedQRCodes() {
            const selectedContent = this.generatedQRCodes
                .filter(item => this.selectedItems[item.location])
                .map(item => {
                    const element = this.$refs.qrCodesContainer.querySelector(`[data-location="${item.location}"]`);
                    return element ? element.outerHTML : '';
                })
                .join('');

            this.printContent(selectedContent);
        },

        // 인쇄 처리 (10개씩 페이지 분할)
        printContent(content) {
            const printStyles = `
          @page {
            size: A4;
            margin: 5mm;
          }
          
          body {
            margin: 0;
            padding: 0;
            width: 200mm;
          }
          
          .location-qr-tag {
            width: 80mm;
            height: 45mm;
            border: 2px solid #fb8b24;
            border-radius: 8px;
            padding: 15px;
            display: flex;
            position: relative;
            background: white;
            box-sizing: border-box;
            page-break-inside: avoid;
            margin-bottom: 2mm;
          }
          
          .qr-tag-content {
            display: flex;
            width: 100%;
            align-items: center;
            justify-content: center;
            position: relative;
          }
          
          .location-info {
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            text-align: center;
          }
          
          .location-name {
            font-size: 80px;
            font-weight: bold;
            line-height: 1.2;
          }
          
          .qr-code-area {
            position: absolute;
            top: -5px;
            right: -5px;
            width: 15mm;
            height: 15mm;
            display: flex;
            align-items: center;
            justify-content: center;
            background: white;
            border: 1px solid #ddd;
            border-radius: 4px;
          }
          
          .location-qr-code {
            width: 18mm !important;
            height: 18mm !important;
            object-fit: contain;
          }
          
          /* 체크박스 숨기기 */
          .select-checkbox {
            display: none !important;
          }
          
          /* 페이지별 컨테이너 */
          .page-container {
            display: grid;
            grid-template-columns: repeat(2, 95mm);
            grid-template-rows: repeat(5, auto);
            gap: 3mm;
            padding: 2mm;
            justify-content: center;
            min-height: 280mm;
            page-break-inside: avoid;
          }
        
          /* 첫 번째 페이지가 아닌 경우 페이지 브레이크 */
          .page-container + .page-container {
            page-break-before: always;
          }
        `;

            // 컨텐츠를 페이지별로 분할 (10개씩)
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = content;
            const qrTagElements = tempDiv.querySelectorAll('.location-qr-tag');

            let paginatedContent = '';
            const itemsPerPage = 10;

            for (let i = 0; i < qrTagElements.length; i += itemsPerPage) {
                const pageItems = Array.from(qrTagElements).slice(i, i + itemsPerPage);
                const pageContent = pageItems.map(item => item.outerHTML).join('');

                paginatedContent += `<div class="page-container">${pageContent}</div>`;
            }

            const printWindow = window.open('', '_blank');
            if (printWindow) {
                const html = `
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <title>위치 QR 코드 인쇄</title>
                        <meta charset="utf-8">
                        <style>${printStyles}</style>
                    </head>
                    <body>
                        ${paginatedContent}
                    </body>
                    </html>
                `;

                printWindow.document.open();
                printWindow.document.write(html);
                printWindow.document.close();

                printWindow.onload = function () {
                    setTimeout(() => {
                        printWindow.print();
                    }, 500);
                };
            } else {
                this.$swal.fire({
                    title: '오류',
                    text: '팝업이 차단되어 있습니다. 팝업 차단을 해제해주세요.',
                    icon: 'error',
                    confirmButtonText: '확인'
                });
            }
        },

        // 전체 선택/해제
        toggleSelectAll() {
            this.generatedQRCodes.forEach(item => {
                this.selectedItems[item.location] = this.selectAll;
            });
            this.selectedItems = { ...this.selectedItems };
        },

        // 전체 선택 상태 업데이트
        updateSelectAll() {
            const totalItems = this.generatedQRCodes.length;
            const selectedCount = Object.values(this.selectedItems).filter(value => value).length;
            this.selectAll = totalItems === selectedCount && totalItems > 0;
        },

        // ESC 키 처리
        handleEscKey(event) {
            if (event.key === 'Escape') {
                this.$emit('close');
            }
        }
    }
};
</script>

<style scoped>
/* 모달 기본 스타일 */
.modal {
    display: flex;
    position: fixed;
    z-index: 1000;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    overflow: auto;
    background-color: rgba(0, 0, 0, 0.4);
    justify-content: center;
    align-items: center;
}

.modal-content {
    background-color: #fefefe;
    padding: 20px;
    border: 1px solid #888;
    border-radius: 8px;
    width: 90%;
    max-width: 900px;
    max-height: 90vh;
    overflow-y: auto;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid #eee;
}

.modal-header h2 {
    margin: 0;
}

.close {
    font-size: 35px;
    font-weight: bold;
    color: #888;
    cursor: pointer;
}

.close:hover {
    color: #fb8b24;
}

/* 위치 입력 영역 */
.location-input-container {
    display: flex;
    align-items: center;
    gap: 8px;
}

.location-prefix-input {
    width: 60px;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    text-align: center;
    font-weight: bold;
}

.separator {
    font-size: 18px;
    font-weight: bold;
    margin: 0 5px;
}

.location-number-input {
    width: 80px;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    text-align: center;
    font-weight: bold;
}

.location-prefix-input:focus,
.location-number-input:focus {
    outline: none;
    border-color: #fb8b24;
    box-shadow: 0 0 0 2px rgba(15, 76, 92, 0.1);
}

.input-group {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 15px;
}

.input-group label {
    font-weight: bold;
    min-width: 120px;
}

.add-btn {
    padding: 8px 16px;
    background-color: #0f4c5c;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: bold;
    white-space: nowrap;
    margin-left: 15px;
}

.add-btn:hover {
    background-color: #ff8533;
}

.location-list h4 {
    margin: 0 0 10px 0;
}

.location-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.location-tag {
    background-color: #fb8b24;
    color: white;
    padding: 6px 10px;
    border-radius: 20px;
    font-size: 15px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.remove-btn {
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    font-size: 20px;
    font-weight: bold;
    padding-bottom: 4px;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.remove-btn:hover {
    color: red;
}

/* 인쇄 컨트롤 */
.print-controls {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 20px;
    margin-bottom: 20px;
    padding: 15px;
    background-color: #f8f9fa;
    border-radius: 8px;
}

.print-controls button {
    padding: 10px 16px;
    cursor: pointer;
    background-color: #FF6B00;
    color: white;
    border: none;
    border-radius: 4px;
    font-weight: bold;
    transition: background-color 0.2s ease;
}

/* 기본 QR 생성 버튼 스타일 */
.default-qr-btn {
    background-color: #0f4c5c !important;
}

.default-qr-btn:hover {
    background-color: #0a3b47 !important;
}

.print-controls button:disabled {
    background-color: #ccc;
    cursor: not-allowed;
}

.print-controls button:hover:not(:disabled) {
    background-color: #ff8533;
}

.select-all {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 5px;
    cursor: pointer;
    font-weight: bold;
}

.select-all input {
    width: 18px;
    height: 18px;
    cursor: pointer;
}

/* QR 코드 컨테이너 */
.qr-codes-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 15px;
    padding: 10px;
}

.location-qr-tag {
    border: 2px solid #fb8b24;
    border-radius: 8px;
    padding: 15px;
    background: white;
    position: relative;
    transition: box-shadow 0.2s ease;
}

.location-qr-tag:hover {
    box-shadow: 0 4px 12px rgba(15, 76, 92, 0.15);
}

.select-checkbox {
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 1;
}

.select-checkbox input {
    width: 18px;
    height: 18px;
    cursor: pointer;
}

.qr-tag-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
}

.location-info {
    flex: 1;
}

.location-title {
    font-size: 14px;
    color: #666;
    margin-bottom: 5px;
}

.location-name {
    font-size: 24px;
    font-weight: bold;
}

.qr-code-area {
    width: 80px;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #eee;
    border-radius: 4px;
}

.location-qr-code {
    width: 100%;
    height: 100%;
    object-fit: contain;
}
</style>